import { useQuery } from '@tanstack/react-query';
import { useFetch } from '@/hooks/useFetch';
import type {
  GetUserExportSettingsResponse,
  UserExportSettingsByScope,
  UserExportSettingsInner,
} from '@/types/userExportSettingsApiTypes';
import { USER_EXPORT_SETTINGS_QUERY_KEY } from '../../consts/client-detail';

function unwrapRawExportSettingsBlob(data: GetUserExportSettingsResponse['data']): Record<string, unknown> {
  if (data && typeof data === 'object' && 'settings' in data && data.settings && typeof data.settings === 'object') {
    return data.settings as unknown as Record<string, unknown>;
  }
  if (data && typeof data === 'object' && 'include_basic_information' in data) {
    return data as unknown as Record<string, unknown>;
  }
  return {};
}

function splitStoredExportSettings(raw: Record<string, unknown>): {
  defaultSlice: Partial<UserExportSettingsInner>;
  groupViewSlice: Partial<UserExportSettingsInner> | undefined;
} {
  const dv = raw.default_view;
  const gv = raw.group_view;

  if (dv !== undefined && dv !== null && typeof dv === 'object' && !Array.isArray(dv)) {
    const groupViewSlice =
      gv !== undefined && gv !== null && typeof gv === 'object' && !Array.isArray(gv)
        ? (gv as Partial<UserExportSettingsInner>)
        : undefined;

    return {
      defaultSlice: dv as Partial<UserExportSettingsInner>,
      groupViewSlice,
    };
  }

  const { group_view: gvLegacy, default_view: _legacyDv, ...rest } = raw;
  const defaultSlice = rest as Partial<UserExportSettingsInner>;
  const groupViewSlice =
    gvLegacy !== undefined && gvLegacy !== null && typeof gvLegacy === 'object' && !Array.isArray(gvLegacy)
      ? (gvLegacy as Partial<UserExportSettingsInner>)
      : undefined;

  return { defaultSlice, groupViewSlice };
}

function mergeExportSettingsFromApi(server: Partial<UserExportSettingsInner>): UserExportSettingsInner {
  const d: UserExportSettingsInner = {
    type: 'PDF',
    include_allergies_dietary_restrictions: false,
    include_loyalty_programs: false,
    include_emergency_contact: false,
    include_flight_preferences: false,
    include_basic_information: {
      toggle: false,
      first_name: false,
      middle_name: false,
      last_name: false,
      primary_email: false,
      date_of_birth: false,
      phone: false,
      gender: false,
      home_airports: false,
      age: false,
      relationship_to_primary: false,
    },
    include_address: { toggle: false, mailing: false, billing: false },
    include_important_dates: { toggle: false, date_of_birth: false, confirmation_ceremony: false },
    include_important_travel_data: { toggle: false, global_entry_number: false, known_traveler_number: false },
    include_travel_preferences_by_category: {
      toggle: false,
      travel: false,
      lifestyle: false,
      stay: false,
      personal: false,
    },
    include_related_contacts: false,
    include_notes: false,
    include_tasks: false,
    include_client_documents: false,
    include_passport_details: false,
    include_passport_details_passport_number: false,
    include_passport_details_document_date_of_birth: false,
    include_passport_details_expiry_date: false,
    include_comfort_related_full_name: false,
    include_comfort_related_height: false,
    include_comfort_related_weight: false,
    include_comfort_related_details: false,
    include_passport_files_as_separate_pdfs: false,
    include_loyalty_program_full_name: false,
    include_loyalty_program_program: false,
    include_loyalty_program_number: false,
  };

  const passportBundleOn = server.include_passport_details === true;
  const hasExplicitPassportColumns =
    server.include_passport_details_passport_number !== undefined ||
    server.include_passport_details_document_date_of_birth !== undefined ||
    server.include_passport_details_expiry_date !== undefined;

  const comfortLegacyPersonal = server.include_travel_preferences_by_category?.personal === true;
  const hasExplicitComfortColumns =
    server.include_comfort_related_full_name !== undefined ||
    server.include_comfort_related_height !== undefined ||
    server.include_comfort_related_weight !== undefined ||
    server.include_comfort_related_details !== undefined;

  const loyaltyBundleOn = server.include_loyalty_programs === true;
  const hasExplicitLoyaltyColumns =
    server.include_loyalty_program_full_name !== undefined ||
    server.include_loyalty_program_program !== undefined ||
    server.include_loyalty_program_number !== undefined;

  const basicInfo = server.include_basic_information;
  const legacyFullNameOn =
    basicInfo !== undefined &&
    basicInfo !== null &&
    typeof basicInfo === 'object' &&
    'full_name' in basicInfo &&
    (basicInfo as { full_name?: boolean }).full_name === true;
  const hasExplicitNameColumns =
    basicInfo?.first_name !== undefined ||
    basicInfo?.middle_name !== undefined ||
    basicInfo?.last_name !== undefined;

  const mergedBasicInformation = { ...d.include_basic_information, ...server.include_basic_information };
  if (legacyFullNameOn && !hasExplicitNameColumns) {
    mergedBasicInformation.first_name = true;
    mergedBasicInformation.middle_name = true;
    mergedBasicInformation.last_name = true;
  }

  return {
    type: server.type ?? d.type,
    include_basic_information: mergedBasicInformation,
    include_address: { ...d.include_address, ...server.include_address },
    include_important_dates: { ...d.include_important_dates, ...server.include_important_dates },
    include_important_travel_data: { ...d.include_important_travel_data, ...server.include_important_travel_data },
    include_travel_preferences_by_category: {
      ...d.include_travel_preferences_by_category,
      ...server.include_travel_preferences_by_category,
    },
    include_related_contacts: server.include_related_contacts ?? d.include_related_contacts,
    include_notes: server.include_notes ?? d.include_notes,
    include_tasks: server.include_tasks ?? d.include_tasks,
    include_client_documents: server.include_client_documents ?? d.include_client_documents,
    include_passport_details: server.include_passport_details ?? d.include_passport_details,
    include_passport_details_passport_number: hasExplicitPassportColumns
      ? (server.include_passport_details_passport_number ?? false)
      : passportBundleOn,
    include_passport_details_document_date_of_birth: hasExplicitPassportColumns
      ? (server.include_passport_details_document_date_of_birth ?? false)
      : passportBundleOn,
    include_passport_details_expiry_date: hasExplicitPassportColumns
      ? (server.include_passport_details_expiry_date ?? false)
      : passportBundleOn,
    include_comfort_related_full_name: hasExplicitComfortColumns
      ? (server.include_comfort_related_full_name ?? false)
      : comfortLegacyPersonal,
    include_comfort_related_height: hasExplicitComfortColumns
      ? (server.include_comfort_related_height ?? false)
      : comfortLegacyPersonal,
    include_comfort_related_weight: hasExplicitComfortColumns
      ? (server.include_comfort_related_weight ?? false)
      : comfortLegacyPersonal,
    include_comfort_related_details: hasExplicitComfortColumns
      ? (server.include_comfort_related_details ?? false)
      : comfortLegacyPersonal,
    include_allergies_dietary_restrictions:
      server.include_allergies_dietary_restrictions ?? d.include_allergies_dietary_restrictions,
    include_loyalty_programs: server.include_loyalty_programs ?? d.include_loyalty_programs,
    include_loyalty_program_full_name: hasExplicitLoyaltyColumns
      ? (server.include_loyalty_program_full_name ?? false)
      : loyaltyBundleOn,
    include_loyalty_program_program: hasExplicitLoyaltyColumns
      ? (server.include_loyalty_program_program ?? false)
      : loyaltyBundleOn,
    include_loyalty_program_number: hasExplicitLoyaltyColumns
      ? (server.include_loyalty_program_number ?? false)
      : loyaltyBundleOn,
    include_emergency_contact: server.include_emergency_contact ?? d.include_emergency_contact,
    include_flight_preferences: server.include_flight_preferences ?? d.include_flight_preferences,
    include_passport_files_as_separate_pdfs:
      server.include_passport_files_as_separate_pdfs ?? d.include_passport_files_as_separate_pdfs,
  };
}

export const useGetUserExportSettingsQuery = () => {
  const { doFetch: getExportSettings } = useFetch<void, GetUserExportSettingsResponse>();

  return useQuery({
    queryKey: [USER_EXPORT_SETTINGS_QUERY_KEY],
    queryFn: async (): Promise<UserExportSettingsByScope> => {
      const res = await getExportSettings('/api/user/export/settings');
      const raw = unwrapRawExportSettingsBlob(res.data);
      const { defaultSlice, groupViewSlice } = splitStoredExportSettings(raw);
      const defaultMerged = mergeExportSettingsFromApi(defaultSlice);
      const groupViewMerged = mergeExportSettingsFromApi(groupViewSlice ?? defaultSlice);

      return { default: defaultMerged, groupView: groupViewMerged };
    },
  });
};